import React from "react";
import { FullAddressWithName } from "./fullAddresswithName";
import "../assets/css/invoice-print.css";
import { formatCurrency } from "../utils/formatCurrency";
import { numberToIndianWords } from "../utils/numberToWords";
import QRCodeGenerator from "./QRCodeGenerator";
import { getStateCode } from "../utils/gst";

const PrintableInvoice = ({
  invoice,
  bankDetails,
  duplicate = false,
  triplicate = false,
}) => {
  const renderAddress = (title, address, includeGst = false) => (
    <div className="address-block">
      {title && <strong>{title}</strong>}
      <FullAddressWithName address={address} includeGst={includeGst} />
    </div>
  );

  const totalAmount = invoice.lines.reduce(
    (sum, line) => sum + parseFloat(line.line_total),
    0
  );
  const amountInWords = (total) => {
    return numberToIndianWords(Math.round(total));
  };

  return (
    <div
      className={`invoice-print ${
        duplicate ? "duplicate" : triplicate ? "triplicate" : ""
      }`}
    >
      <header>
        <div className="header-left">
          <img
            src="https://www.apogeemattress.com/logo.svg"
            alt="Company Logo"
            className="logo"
          />
          {renderAddress("", invoice.branch?.address, true)}
        </div>
        <div className="header-right">
          <h2>Tax Invoice</h2>
          <p>
            <strong>Invoice No:</strong> {invoice.invoice_no}
          </p>
          <p>
            <strong>Invoice Date:</strong> {invoice.date}
          </p>
          <p>
            <strong>Order Ref:</strong> {invoice.order_reference}
          </p>
          {invoice?.vehicle_no && (
            <p>
              <strong>Vehicle No:</strong> {invoice.vehicle_no}
            </p>
          )}
          {invoice?.eway_bill_no && (
            <p>
              <strong>E-WAY Bill:</strong> {invoice.eway_bill_no}
            </p>
          )}
        </div>
      </header>

      <section className="address-section">
        {renderAddress("Billing Address", invoice.billing_address, true)}
        {renderAddress("Shipping Address", invoice.shipping_address)}
        <div className="address-block">
          <p>
            <strong>Customer State Code:</strong>{" "}
            {getStateCode(invoice.shipping_address?.state)}
          </p>
          <p>
            <strong>Place of Supply:</strong> {invoice.shipping_address?.state}
          </p>
        </div>
      </section>

      <table className="invoice-table">
        <thead>
          <tr>
            <th>Sl. No.</th>
            <th>Product</th>
            <th>HSN</th>
            <th>Rate</th>
            <th>Disc. %</th>
            <th>Qty</th>
            <th>Amt</th>
            <th>Tax Amt</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line, index) => (
            <tr key={line.id}>
              <td>{index + 1}</td>
              <td>{line.product.display_name}</td>
              <td>{line.product.hsn}</td>
              <td>{formatCurrency(line.unit_rate)}</td>
              <td>{line.discount_percent}%</td>
              <td>{line.quantity}</td>
              <td>{formatCurrency(line.line_total - line.tax_amount)}</td>
              <td>{formatCurrency(line.tax_amount)}</td>
              <td>{formatCurrency(line.line_total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="8" className="text-right">
              <strong>Total</strong>
            </td>
            <td>
              <strong>{formatCurrency(totalAmount)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>

      <div className="amount-in-words">
        <strong>Amount in words:</strong> {amountInWords(totalAmount)}
      </div>

      <section className="hsn-breakup">
        <h4>HSN-wise Tax Breakup</h4>
        <table>
          <thead>
            <tr>
              <th rowSpan={2}>HSN</th>
              <th rowSpan={2}>Taxable Value</th>
              <th colSpan={2}>CGST</th>
              <th colSpan={2}>SGST</th>
              <th colSpan={2}>IGST</th>
            </tr>
            <tr>
              {/* <th></th>
              <th></th> */}
              <th>%ge</th>
              <th>Amount</th>
              <th>%ge</th>
              <th>Amount</th>
              <th>%ge</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.hsn_breakup.map((item, idx) => (
              <tr key={idx}>
                <td>{item.hsn}</td>
                <td>{formatCurrency(item.total_taxable_value)}</td>
                <td>{item.taxes.CGST_PERC || 0}%</td>
                <td>{formatCurrency(item.taxes.CGST || 0)}</td>
                <td>{item.taxes.SGST_PERC || 0}%</td>
                <td>{formatCurrency(item.taxes.SGST || 0)}</td>
                <td>{item.taxes.IGST_PERC || 0}%</td>
                <td>{formatCurrency(item.taxes.IGST || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bank-and-qr">
        <div className="bank-details">
          <h4>Bank Details</h4>
          <p>
            <strong>Account Name:</strong> {bankDetails.accountName}
            <br />
            <strong>Bank Name:</strong> {bankDetails.bankName}
            <br />
            <strong>Account Number:</strong> {bankDetails.accountNumber}
            <br />
            <strong>IFSC Code:</strong> {bankDetails.ifsc}
          </p>
        </div>
        <div className="qr-code">
          <div className="qr-code-inner">
            {/* <QRCodeGenerator
              value={`upi://pay?pa=1000220513000051.9337837778@idbi&pn=Apogee Mattress&am=${totalAmount}&cu=INR`}
              size={64}
            /> */}
            <QRCodeGenerator
              value={`upi://pay?pa=1000220513000051.9337837778@idbi&pn=Apogee Mattress&cu=INR`}
              size={64}
            />
            <span>Scan to pay</span>
          </div>
        </div>
      </section>
      <section className="footer-notes">
        <h6>Terms of Sale</h6>
        <ol>
          <li>
            Payments must be made within 30 days. Interest at 24% will be
            charged on overdue amounts.
          </li>
          <li>
            Cash discount available: 3% if paid within 7 days, 2% within 15
            days, and 1% within 21 days.
          </li>
          <li>
            All payments should be made by RTGS/NEFT/UPI/DD/Account Payee
            Cheque. No cash transaction with our sales executive.
          </li>
          <li>
            Any complaints regarding quality of goods should be lodged within 1
            day from the date of supply.
          </li>
          <li>
            Shortage quantity should be mentioned in acknowledgement copy.
          </li>
        </ol>
      </section>

      <footer className="eoe">
        <p>
          <strong>E. & O.E.</strong>
        </p>
        {/* <p>
          <strong>CIN:</strong> U36109OR2021PTC037284
        </p>
        <p>
          <strong>Udyam Registration No:</strong> UDYAM-OD-19-0015642
        </p> */}
        <div className="cin-udyam">
          <span>
            <strong>CIN: </strong> U17299OR2021PTC037693
          </span>
          <span>
            <strong>MSME-UDYAM: </strong>OD-19-0015642
          </span>
        </div>
        <p className="signature-note">
          This is a computer generated invoice and does not require a signature.
        </p>
      </footer>
    </div>
  );
};

export default PrintableInvoice;
